import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash, FaPaperPlane } from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../services/api';
import WishFormFixed from './WishFormFixed';
import './Wishes.css';

const WishesList = () => {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastId, setLastId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [editingWish, setEditingWish] = useState(null);
  const { user } = useUserAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchWishes();

    // Add blue background to the body element directly
    document.body.style.backgroundColor = 'var(--tertiary-color)';

    // Cleanup function to reset body color when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  const fetchWishes = async (nextPage = false) => {
    try {
      setLoading(true);
      setError('');

      let url = `/wishes?limit=10`;
      if (nextPage && lastId) {
        url += `&lastId=${lastId}`;
      } else if (nextPage) {
        return;
      }

      const response = await api.get(url);
      const fetchedWishes = response.data.wishes;
      const pagination = response.data.pagination;

      if (nextPage) {
        setWishes(prev => [...prev, ...fetchedWishes]);
      } else {
        setWishes(fetchedWishes);
      }

      setLastId(pagination.lastId);
      setHasMore(pagination.hasMore);
    } catch (err) {
      console.error('Error fetching wishes:', err);
      setError('Failed to load wishes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (wishId) => {
    if (!user) return;

    if (window.confirm(t('confirmDelete'))) {
      try {
        await api.delete(`/wishes/${wishId}`);

        // Remove from local state
        setWishes(wishes.filter(wish => wish.id !== wishId));
      } catch (err) {
        console.error('Error deleting wish:', err);
        setError('Failed to delete wish. Please try again.');
      }
    }
  };

  const handleEdit = (wish) => {
    setEditingWish(wish);
    // Scroll to form
    document.getElementById('wish-form-section').scrollIntoView({ behavior: 'smooth' });
  };

  const handleWishSubmitted = (newWish) => {
    if (editingWish) {
      // Update existing wish in the list
      setWishes(wishes.map(wish =>
          wish.id === newWish.id ? newWish : wish
      ));
      setEditingWish(null);
    } else {
      // Add new wish to the top of the list
      setWishes([newWish, ...wishes]);
    }
  };

  const handleCancelEdit = () => {
    setEditingWish(null);
  };

  if (loading && wishes.length === 0) {
    return (
        <div className="wishes-container">
          <Container className="text-center py-5">
            <Spinner animation="border" role="status" className="text-white">
              <span className="visually-hidden">{t('loading')}</span>
            </Spinner>
          </Container>
        </div>
    );
  }

  return (
      <div className="wishes-container">
        <Container className="py-5">
          <div className="text-center mb-5">
            <h2>{t('wishes.title')}</h2>
            <p className="lead">{t('wishes.subtitle')}</p>
          </div>

          <Row>
            <Col lg={4} id="wish-form-section">
              <div className="sticky-top pt-3" style={{ top: '80px' }}>
                <WishFormFixed
                    editingWish={editingWish}
                    onWishSubmitted={handleWishSubmitted}
                    onCancelEdit={handleCancelEdit}
                />
              </div>
            </Col>

            <Col lg={8}>
              {error && <Alert variant="danger">{error}</Alert>}

              {wishes.length === 0 && !loading ? (
                  <div className="text-center py-5">
                    <p>{t('wishes.noWishes')}</p>
                    <FaPaperPlane className="no-wishes-icon" />
                    <p>{t('wishes.beFirstToLeaveWish')}</p>
                  </div>
              ) : (
                  <div className="wishes-list">
                    {wishes.map(wish => (
                        <Card key={wish.id} className="wish-card mb-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between">
                              <Card.Title className="wish-author">
                                {wish.userName}
                              </Card.Title>
                              {user && (user.uid === wish.userId || user.role === 'admin') && (
                                  <div className="wish-actions">
                                    <Button
                                        variant="link"
                                        className="edit-btn p-0 me-2"
                                        onClick={() => handleEdit(wish)}
                                    >
                                      <FaEdit />
                                    </Button>
                                    <Button
                                        variant="link"
                                        className="delete-btn p-0"
                                        onClick={() => handleDelete(wish.id)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                              )}
                            </div>

                            <Card.Text className="wish-message">
                              {wish.message}
                            </Card.Text>

                            <div className="text-muted wish-date">
                              <span title={new Date(wish.createdAt).toLocaleString()}>
                                {new Date(wish.createdAt).toLocaleDateString()} {new Date(wish.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                              {wish.createdAt !== wish.updatedAt && ` · ${t('wishes.edited')}`}
                            </div>
                          </Card.Body>
                        </Card>
                    ))}

                    {hasMore && (
                        <div className="text-center mt-4">
                          <Button
                              variant="outline-light"
                              onClick={() => fetchWishes(true)}
                              disabled={loading}
                          >
                            {loading ? (
                                <>
                                  <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                  />
                                  {t('loading')}
                                </>
                            ) : (
                                t('wishes.loadMore')
                            )}
                          </Button>
                        </div>
                    )}
                  </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
  );
};

export default WishesList;